import { HttpErrorResponse } from "@angular/common/http";

export class RouteNotFoundError extends Error {
  constructor(message?: string) {
    super(message ?? "No valid routes could be determined");
    this.name = "RouteNotFoundError";
    Object.setPrototypeOf(this, RouteNotFoundError.prototype);
  }
}

/**
 * Thrown in http service:
 *
 * - From an angular HttpErrorResponse `HttpError.fromHttpErrorResponse(err)`
 * - From a string or Error `HttpError.fromErrorOrString(err)`
 */
export class HttpError extends Error {
  public status?: number;
  public statusText?: string;
  public url?: string;
  public originalError?: any;

  private constructor(
    message: string,
    status?: number,
    statusText?: string,
    url?: string | null,
    originalError?: Error
  ) {
    super(message);
    this.name = "HttpError";

    // Explicitly define properties to ensure they are enumerable
    Object.defineProperties(this, {
      status: { value: status, enumerable: true },
      statusText: { value: statusText, enumerable: true },
      url: { value: url ?? undefined, enumerable: true },
    });

    if (originalError) {
      this.originalError = originalError;
    }

    if ("captureStackTrace" in Error) {
      (Error as any).captureStackTrace(this, HttpError);
    }
  }

  static fromHttpErrorResponse(
    httpErrorResponse: HttpErrorResponse
  ): HttpError {
    const message =
      httpErrorResponse.error?.message ??
      `HTTP ${httpErrorResponse.status}: ${httpErrorResponse.statusText}`;

    const err = new HttpError(
      message,
      httpErrorResponse.status,
      httpErrorResponse.statusText,
      httpErrorResponse.url,
      httpErrorResponse
    );

    err.originalError = httpErrorResponse;

    return err;
  }

  static fromErrorOrString(
    input: Error | string,
    status?: number,
    statusText?: string,
    url?: string | null
  ): HttpError {
    const message = input instanceof Error ? input.message : input;
    const originalError = input instanceof Error ? input : undefined;

    const err = new HttpError(message, status, statusText, url, originalError);
    
    if (input instanceof Error) {
      err.stack = input.stack;
    }

    err.originalError = {
      ...err.originalError,
      message,
      status,
      statusText,
      url,
    };

    return err;
  }
}

/**
 * Thrown from permission check or within app in cases where
 * user is not enabled or is missing rights to view a page.
 */
export class AccessDeniedError extends Error {
  public requestedEndpoint?: string;
  public requestedScreen?: string;

  constructor(props?: _AccessDeniedErrorProps) {
    super(props?.message ?? "Access Denied");
    this.name = "AccessDeniedError";
    this.requestedEndpoint = props?.endpoint;
    this.requestedScreen = props?.screen;

    if ("captureStackTrace" in Error) {
      (Error as any).captureStackTrace(this, AccessDeniedError);
    }
  }
}

interface _AccessDeniedErrorProps {
  message?: string;
  endpoint?: string;
  screen?: string;
}
